import { useEffect, useState } from "react";
import { OAUTH, OAUTH_PROVIDERS, type OAuthProvider } from "@oshimap/config";
import { ep, session } from "../../lib/api";

const LABEL: Record<OAuthProvider, string> = { naver: "네이버", kakao: "카카오", google: "구글" };

/**
 * 소셜 로그인 복귀 처리 (실측 계약).
 * 제공자 → 백엔드 콜백 → 백엔드가 `/?oauth=<provider>` 로 브라우저를 되돌린다.
 * 토큰은 쿠키 세션에서 한 번만 꺼낼 수 있으므로, 실패하든 성공하든 쿼리를 즉시 지운다.
 * 지우지 않으면 새로고침 한 번에 "로그인 실패" 가 다시 뜬다.
 */
export function useOAuthReturn(): { error: string | null; dismiss: () => void } {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const ok = url.searchParams.get(OAUTH.returnParam);
    const failed = url.searchParams.get(OAUTH.errorParam);
    if (!ok && !failed) return;

    url.searchParams.delete(OAUTH.returnParam);
    url.searchParams.delete(OAUTH.errorParam);
    window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);

    if (failed) {
      const provider = OAUTH_PROVIDERS.find((p) => failed.startsWith(p));
      setError(`${provider ? LABEL[provider] : "소셜"} 로그인을 마치지 못했어요.`);
      return;
    }
    const provider = OAUTH_PROVIDERS.find((p) => p === ok);
    if (!provider) return;

    let live = true;
    void (async () => {
      try {
        const r = await ep.oauthSession(provider);
        if (!live) return;
        await session.signIn(r.token, r.user);
      } catch {
        if (live) setError(`${LABEL[provider]} 로그인을 마치지 못했어요. 다시 시도해 주세요.`);
      }
    })();
    return () => { live = false; };
  }, []);

  return { error, dismiss: () => setError(null) };
}
